import { Transition, TransitionChild } from '@headlessui/react';
import Icon from '@/core/icon';
import Avatar from '@/core/avatar.js';
import { getPathByLang } from '@/helpers/router';
import { ucfirst } from '@/helpers/common';
import useCss1024 from './helpers/useCss1024';
import SearchComponent from './search.js';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import { lang } from '@/helpers/translate';
import { useHeaderScroll } from './hooks/useHeaderScroll';

// Composant pour le menu de transition
const TransitionMenu = ({ show, onClose, children }) => (
  <Transition
    as="div"
    show={show}
    className="fixed bottom-0 z-40 text-center h-100v"
    role="menu"
    aria-orientation="vertical"
    aria-labelledby="user-menu"
  >
    <TransitionChild
      as="div"
      enter="transition-opacity ease-in duration-200"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity ease-out duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      className="absolute top-0 bottom-0 z-50 w-100v bg-purple-6/80 h-100v"
      onClick={onClose}
    />
    <TransitionChild
      as="div"
      enter="transition ease-in-out duration-200 transform"
      enterFrom="translate-y-full"
      enterTo="translate-y-0"
      leave="transition ease-in-out duration-200 transform"
      leaveFrom="translate-y-0"
      leaveTo="translate-y-full"
      className="absolute bottom-0 flex flex-col h-auto px-4 py-4 pb-20 bg-white rounded-t-lg z-60 shadow-catridge w-100v text-dark"
    >
      {children}
    </TransitionChild>
  </Transition>
);

// Composant pour les boutons de navigation
const NavButton = ({ onClick, icon, label }) => (
  <button 
    className="flex flex-col items-center px-1 py-2 min-w-16 text-dark" 
    onClick={onClick}
  >
    <div className="flex items-center h-7.5">
      <Icon name={icon} className="block text-white w-7 h-7" />
    </div>
    <span className="pt-1 text-xs text-white/60">{label}</span>
  </button>
);

const HeaderMobile = ({
  t,
  isAuthenticated,
  userConnected,
  open,
  open2,
  open3,
  setOpen,
  setOpen2,
  setOpen3,
  disconnect,
  openLogin,
  comefrom,
  headerCorpoLinksD,
  handleMenuToggle
}) => {
  const css1024 = useCss1024();
  const isCorpo = userConnected?.corpo?.ref;
  const langCurrent = lang();
  const { mobileHeaderRef } = useHeaderScroll();

  const renderAuthMenus = () => {
    if (!isAuthenticated) return null;

    return (
      <>
        <TransitionMenu show={open2} onClose={() => setOpen2(false)}>
          <a href={getPathByLang('dash_host_book', langCurrent)} className="px-4 py-2 mb-2 text-black rounded-lg bg-purple-0" role="menuitem">
            {t('header:booking')}
          </a>
          <a className="px-4 py-2 mb-2 text-black rounded-lg bg-purple-0" role="menuitem" href={getPathByLang('dash_host_spaces', langCurrent)}>
            {t('header:host.listing')}
          </a>
          <a className="px-4 py-2 mb-2 text-black rounded-lg bg-purple-0" role="menuitem" href={getPathByLang('add_space_v3', langCurrent)}>
            {t('header:host.add_space')}
          </a>
          <a href={`${process.env.NEXT_PUBLIC_URL_V4}/blog/les-10-clefs-du-succes-officeriders`} className="px-4 py-2 mb-2 text-black rounded-lg bg-purple-0" role="menuitem">
            {t('header:host.host_guide')}
          </a>
        </TransitionMenu>

        <TransitionMenu show={open} onClose={() => setOpen(false)}>
          <a className="px-4 py-2 mb-2 text-black rounded-lg bg-purple-0" role="menuitem" href={getPathByLang('user_account_v3', langCurrent)}>
            {t('header:user.account')}
          </a>
          <div className="px-4 py-2 mb-2 text-black rounded-lg bg-purple-0" role="menuitem" onClick={disconnect}>
            {t('header:user.logout')}
          </div>
        </TransitionMenu>

        {isCorpo && (
          <TransitionMenu show={open3} onClose={() => setOpen3(false)}>
            {headerCorpoLinksD}
          </TransitionMenu>
        )}
      </>
    );
  };

  return (
    <>
      {renderAuthMenus()}
      <div ref={mobileHeaderRef} id="componentHeaderContainer1024" className="fixed bottom-0 z-50 w-full mx-auto transition-all shadow-button from-purple-6/80 to-purple-6/80 bg-gradient-to-b backdrop-blur-lg">
        <div className={css1024}>
          <div className="flex items-center justify-center w-full">
            <div className="grid items-center justify-center w-full grid-flow-col auto-cols-max gap-x-1">
              {comefrom !== 'search' && <SearchComponent notdesktop />}
              
              {isAuthenticated && userConnected?.hasSpaces && (
                <NavButton 
                  onClick={() => handleMenuToggle(open2, setOpen2)}
                  icon="househome"
                  label={t('header:host.host')}
                />
              )}

              {isAuthenticated && userConnected?.hasBookings && (
                <a className="flex flex-col items-center px-1 py-2 h8 min-w-16 text-dark" href={getPathByLang('dash_rider_book', langCurrent)}>
                  <div className="flex items-center h-7.5">
                    <Icon name="chat" className="block text-white w-7 h-7" />
                  </div>
                  <span className="pt-1 text-xs text-white/60">{t('header:booking')}</span>
                </a>
              )}

              {isCorpo && (
                <button 
                  className="flex flex-col items-center px-1 py-2 min-w-16 text-dark" 
                  onClick={() => handleMenuToggle(open3, setOpen3)}
                >
                  <Avatar className="relative size-7.5 overflow-hidden text-white rounded-lg" renderAvatar user={userConnected} />
                  <p className="pt-1 text-xs truncate text-white/60 max-w-16">{ucfirst(userConnected?.corpo?.name)}</p>
                </button>
              )}

              {isAuthenticated ? (
                <button 
                  className="flex flex-col items-center px-1 py-2 min-w-16 text-dark" 
                  id="user-menu" 
                  aria-haspopup="true" 
                  onClick={() => handleMenuToggle(open, setOpen)}
                >
                  <Avatar className="relative size-7.5 overflow-hidden text-white rounded-lg" user={userConnected || null} />
                  <p className="pt-1 text-xs truncate text-white/60 max-w-16">{ucfirst(userConnected?.firstname)}</p>
                </button>
              ) : (
                <button 
                  className="flex flex-col items-center px-1 py-2 min-w-16 text-dark" 
                  onClick={openLogin}
                >
                  <Icon name="user" className="block size-7.5 text-white" />
                  <span className="pt-1 text-xs text-white/60">{t('header:connection')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HeaderMobile; 